/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { AssetCardInfo } from "./info";
import { AssetCardService } from "services/asset-card";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  cardNumber: "",
  creationDate: "",
  handoverDocNumber: "",
  handoverDocDate: "",
  assetName: "",
  assetId: "",
  manufacturingCountry: "",
  manufacturingYear: "",
  managementDepartment: "",
  useYear: "",
  capacity: "",
  suspensionDate: "",
  suspensionReason: "",
  costAndDepreciation: [
    {
      docNumber: "",
      costDate: "",
      description: "",
      costValue: 0,
      depreciationYear: "",
      depreciationValue: 0,
      accumulatedValue: 0,
    }
  ],
  attachedTools: [
    {
      ordinalNumber: "",
      name: "",
      unit: "",
      quantity: 0,
      value: 0,
    }
  ],
  reductionDocNumber: "",
  reductionDocDate: "",
  reductionReason: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  reportDate: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function AssetCardDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await AssetCardService.getById(id);
          const apiData = response.data || response;

          const formatDateVN = (iso) => {
            if (!iso) return "";
            if (typeof iso === "string" && iso.includes("/")) return iso;
            const d = new Date(iso);
            return isNaN(d) ? iso : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
          };

          // Parse reportDate sang signingDay/Month/Year if possible
          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || "",
            address: apiData.address || "",
            cardNumber: apiData.cardNumber || "",
            creationDate: formatDateVN(apiData.creationDate),
            handoverDocNumber: apiData.handoverDocNumber || "",
            handoverDocDate: formatDateVN(apiData.handoverDocDate),
            assetName: apiData.assetName || "",
            assetId: apiData.assetId || "",
            manufacturingCountry: apiData.manufacturingCountry || "",
            manufacturingYear: apiData.manufacturingYear || "",
            managementDepartment: apiData.managementDepartment || "",
            useYear: apiData.useYear || "",
            capacity: apiData.capacity || "",
            suspensionDate: formatDateVN(apiData.suspensionDate),
            suspensionReason: apiData.suspensionReason || "",
            costAndDepreciation: apiData.costAndDepreciation?.length > 0 ? apiData.costAndDepreciation : initialValues.costAndDepreciation,
            attachedTools: apiData.attachedTools?.length > 0 ? apiData.attachedTools : initialValues.attachedTools,
            reductionDocNumber: apiData.reductionDocNumber || "",
            reductionDocDate: formatDateVN(apiData.reductionDocDate),
            reductionReason: apiData.reductionReason || "",
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/asset-card");

  const submitForm = async (fValues) => {
    try {
      const num = (v) => (v && v !== "" && !isNaN(v) ? Number(v) : 0);

      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        cardNumber: fValues.cardNumber,
        creationDate: fValues.creationDate,
        handoverDocNumber: fValues.handoverDocNumber,
        handoverDocDate: fValues.handoverDocDate,
        assetName: fValues.assetName,
        assetId: fValues.assetId,
        manufacturingCountry: fValues.manufacturingCountry,
        manufacturingYear: fValues.manufacturingYear,
        managementDepartment: fValues.managementDepartment,
        useYear: fValues.useYear,
        capacity: fValues.capacity,
        suspensionDate: fValues.suspensionDate,
        suspensionReason: fValues.suspensionReason,
        costAndDepreciation: (fValues.costAndDepreciation || []).map(row => ({
          ...row,
          costValue: num(row.costValue),
          depreciationValue: num(row.depreciationValue),
          accumulatedValue: num(row.accumulatedValue),
        })),
        attachedTools: (fValues.attachedTools || []).map(row => ({
          ...row,
          quantity: num(row.quantity),
          value: num(row.value),
        })),
        reductionDocNumber: fValues.reductionDocNumber,
        reductionDocDate: fValues.reductionDocDate,
        reductionReason: fValues.reductionReason,
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await AssetCardService.create(payload);
        toast.success("Tạo thẻ thành công!");
      } else {
        await AssetCardService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/asset-card");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S23-DN</title>
      <style>@media print{@page{size:A4;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <AssetCardInfo formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                        {containView ? (
                          <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Thẻ S23-DN</SoftButton>
                        ) : (
                          <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default AssetCardDetail;
